import vietnameseSeo from './vietnameseSeo.json';

export const SITE_URL = 'https://www.jlbhk.com';
export const SITE_NAME = '金利寶環球 Gold Label Global Limited';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/img/768bd75e75ad5419eb79b56a0fb32913.jpg`;

export const SEO_LANGUAGES = [
  {
    code: 'hk',
    label: '繁體中文',
    htmlLang: 'zh-Hant-HK',
    hreflang: 'zh-Hant-HK',
    ogLocale: 'zh_HK',
    pathPrefix: 'hk',
    contentCode: 'hk',
  },
  {
    code: 'cn',
    label: '简体中文',
    htmlLang: 'zh-Hans-CN',
    hreflang: 'zh-Hans-CN',
    ogLocale: 'zh_CN',
    pathPrefix: 'cn',
    contentCode: 'cn',
  },
  {
    code: 'en',
    label: 'English',
    htmlLang: 'en',
    hreflang: 'en',
    ogLocale: 'en_US',
    pathPrefix: 'en',
    contentCode: 'en',
  },
  {
    code: 'ug',
    label: 'ئۇيغۇرچە',
    htmlLang: 'ug-Arab',
    hreflang: 'ug-Arab',
    ogLocale: 'ug_CN',
    pathPrefix: 'ug',
    contentCode: 'en',
  },
  {
    code: 'vn',
    label: 'Tiếng Việt',
    htmlLang: 'vi-VN',
    hreflang: 'vi-VN',
    ogLocale: 'vi_VN',
    pathPrefix: 'vn',
    contentCode: 'vn',
  },
] as const;

export type SeoLanguageCode = (typeof SEO_LANGUAGES)[number]['code'];
export type SeoContentLanguageCode = (typeof SEO_LANGUAGES)[number]['contentCode'];

export type SeoPage = {
  path: string;
  changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  priority: string;
  title: Record<SeoContentLanguageCode, string>;
  description: Record<SeoContentLanguageCode, string>;
  keywords: Record<SeoContentLanguageCode, string>;
};

type BaseSeoPage = Omit<SeoPage, 'title' | 'description' | 'keywords'> & {
  title: Record<Exclude<SeoContentLanguageCode, 'vn'>, string>;
  description: Record<Exclude<SeoContentLanguageCode, 'vn'>, string>;
  keywords: Record<Exclude<SeoContentLanguageCode, 'vn'>, string>;
};

const BASE_SEO_PAGES: BaseSeoPage[] = [
  {
    path: '/',
    changefreq: 'weekly',
    priority: '1.0',
    title: {
      hk: '金利寶環球｜香港金利寶黃金、白銀及貴金屬交易平台',
      cn: '金利宝环球｜香港金利宝黄金、白银及贵金属交易平台',
      en: 'Gold Label Global Limited | Hong Kong Precious Metals Trading Platform',
    },
    description: {
      hk: '金利寶環球 Gold Label Global Limited（JLBHK）提供香港金利寶黃金、白銀及貴金屬交易相關服務，支援開戶、MT5 平台、出入金流程及市場資訊。',
      cn: '金利宝环球 Gold Label Global Limited（JLBHK）提供香港金利宝黄金、白银及贵金属交易相关服务，支持开户、MT5 平台、出入金流程及市场资讯。',
      en: 'Gold Label Global Limited (JLBHK) provides Hong Kong precious metals trading information, gold and silver market access, MT5 platform support, account opening and deposit/withdrawal guidance.',
    },
    keywords: {
      hk: '金利寶, 金利寶環球, 金利宝环球, 金利宝, 香港金利寶, 金利寶黃金, 香港黃金交易, 黃金交易平台, 白銀交易, 貴金屬交易, Gold Label Global, JLBHK',
      cn: '金利宝, 金利宝环球, 金利寶環球, 金利寶, 香港金利宝, 金利宝黄金, 香港黄金交易, 黄金交易平台, 白银交易, 贵金属交易, Gold Label Global, JLBHK',
      en: 'Gold Label Global Limited, JLBHK, Hong Kong gold trading, precious metals trading, silver trading, MT5 trading platform',
    },
  },
  {
    path: '/about',
    changefreq: 'monthly',
    priority: '0.8',
    title: {
      hk: '關於金利寶環球｜香港金利寶公司簡介',
      cn: '关于金利宝环球｜香港金利宝公司简介',
      en: 'About Gold Label Global Limited | JLBHK Company Profile',
    },
    description: {
      hk: '了解金利寶環球 Gold Label Global Limited 的公司背景、管理團隊、貴金屬交易經驗，以及香港金利寶黃金與白銀服務理念。',
      cn: '了解金利宝环球 Gold Label Global Limited 的公司背景、管理团队、贵金属交易经验，以及香港金利宝黄金与白银服务理念。',
      en: 'Learn about Gold Label Global Limited, the JLBHK company profile, management experience and approach to precious metals trading services.',
    },
    keywords: {
      hk: '關於金利寶, 金利寶環球公司, 香港金利寶, 金利寶黃金, Gold Label Global Limited, JLBHK',
      cn: '关于金利宝, 金利宝环球公司, 香港金利宝, 金利宝黄金, Gold Label Global Limited, JLBHK',
      en: 'About Gold Label Global Limited, JLBHK company, Hong Kong precious metals company',
    },
  },
  {
    path: '/license',
    changefreq: 'monthly',
    priority: '0.8',
    title: {
      hk: '金利寶環球牌照資質｜香港貴金屬交易資質',
      cn: '金利宝环球牌照资质｜香港贵金属交易资质',
      en: 'Licenses and Qualifications | Gold Label Global Limited',
    },
    description: {
      hk: '查看金利寶環球 Gold Label Global Limited 的牌照資質、公司註冊資料及香港貴金屬交易相關資訊。',
      cn: '查看金利宝环球 Gold Label Global Limited 的牌照资质、公司注册资料及香港贵金属交易相关资讯。',
      en: 'View license information, company registration details and qualifications for Gold Label Global Limited.',
    },
    keywords: {
      hk: '金利寶牌照, 金利寶環球牌照, 香港金利寶資質, 貴金屬交易牌照, Gold Label Global license',
      cn: '金利宝牌照, 金利宝环球牌照, 香港金利宝资质, 贵金属交易牌照, Gold Label Global license',
      en: 'Gold Label Global license, JLBHK license, Hong Kong precious metals trading qualifications',
    },
  },
  {
    path: '/platform',
    changefreq: 'monthly',
    priority: '0.7',
    title: {
      hk: '金利寶 MT5 交易平台｜香港黃金白銀交易工具',
      cn: '金利宝 MT5 交易平台｜香港黄金白银交易工具',
      en: 'MT5 Trading Platform | Gold Label Global Limited',
    },
    description: {
      hk: '金利寶環球提供 MT5 交易平台相關資訊，支援黃金、白銀及貴金屬市場分析、圖表工具與交易平台下載。',
      cn: '金利宝环球提供 MT5 交易平台相关资讯，支持黄金、白银及贵金属市场分析、图表工具与交易平台下载。',
      en: 'Explore MT5 trading platform information for gold, silver and precious metals market analysis, charting tools and downloads.',
    },
    keywords: {
      hk: '金利寶MT5, 金利寶交易平台, 黃金交易平台, 白銀交易平台, MT5下載, 貴金屬交易工具',
      cn: '金利宝MT5, 金利宝交易平台, 黄金交易平台, 白银交易平台, MT5下载, 贵金属交易工具',
      en: 'JLBHK MT5, Gold Label Global trading platform, gold trading platform, MT5 download',
    },
  },
  {
    path: '/market',
    changefreq: 'weekly',
    priority: '0.7',
    title: {
      hk: '金利寶黃金市場｜黃金、白銀及貴金屬交易規格',
      cn: '金利宝黄金市场｜黄金、白银及贵金属交易规格',
      en: 'Gold and Silver Markets | Precious Metals Trading Specifications',
    },
    description: {
      hk: '瀏覽金利寶黃金、白銀及貴金屬市場資訊，包括合約詳情、交易時間、保證金要求及風險管理重點。',
      cn: '浏览金利宝黄金、白银及贵金属市场资讯，包括合约详情、交易时间、保证金要求及风险管理重点。',
      en: 'Browse gold, silver and precious metals market information including contract details, trading hours, margin requirements and risk management.',
    },
    keywords: {
      hk: '金利寶黃金, 香港黃金市場, 黃金價格, 白銀價格, 貴金屬合約, 黃金保證金',
      cn: '金利宝黄金, 香港黄金市场, 黄金价格, 白银价格, 贵金属合约, 黄金保证金',
      en: 'gold market, silver market, precious metals contract details, gold margin, JLBHK markets',
    },
  },
  {
    path: '/contact',
    changefreq: 'monthly',
    priority: '0.6',
    title: {
      hk: '聯絡金利寶環球｜香港金利寶客服及開戶查詢',
      cn: '联系金利宝环球｜香港金利宝客服及开户查询',
      en: 'Contact Gold Label Global Limited | JLBHK Support',
    },
    description: {
      hk: '聯絡金利寶環球客服團隊，查詢香港金利寶開戶、交易支援、技術支援、出入金流程及貴金屬服務。',
      cn: '联系金利宝环球客服团队，查询香港金利宝开户、交易支持、技术支持、出入金流程及贵金属服务。',
      en: 'Contact the Gold Label Global Limited support team for account opening, trading support, technical support and precious metals service enquiries.',
    },
    keywords: {
      hk: '聯絡金利寶, 金利寶客服, 香港金利寶開戶, 金利寶環球查詢, 貴金屬客服',
      cn: '联系金利宝, 金利宝客服, 香港金利宝开户, 金利宝环球查询, 贵金属客服',
      en: 'contact JLBHK, Gold Label Global support, Hong Kong precious metals account opening',
    },
  },
  {
    path: '/faq',
    changefreq: 'monthly',
    priority: '0.6',
    title: {
      hk: '金利寶常見問題｜開戶、出入金及黃金交易 FAQ',
      cn: '金利宝常见问题｜开户、出入金及黄金交易 FAQ',
      en: 'JLBHK FAQ | Account Opening, Deposits and Gold Trading',
    },
    description: {
      hk: '金利寶環球常見問題，涵蓋開戶流程、出入金、MT5 平台、黃金白銀交易及貴金屬風險提示。',
      cn: '金利宝环球常见问题，涵盖开户流程、出入金、MT5 平台、黄金白银交易及贵金属风险提示。',
      en: 'Frequently asked questions about JLBHK account opening, deposits and withdrawals, MT5 platform, gold and silver trading and risk notes.',
    },
    keywords: {
      hk: '金利寶FAQ, 金利寶開戶, 金利寶出入金, 金利寶黃金問題, MT5常見問題',
      cn: '金利宝FAQ, 金利宝开户, 金利宝出入金, 金利宝黄金问题, MT5常见问题',
      en: 'JLBHK FAQ, account opening, deposits withdrawals, MT5 FAQ, gold trading FAQ',
    },
  },
  {
    path: '/terms',
    changefreq: 'yearly',
    priority: '0.4',
    title: {
      hk: '金利寶環球用戶使用協議｜法律條款',
      cn: '金利宝环球用户使用协议｜法律条款',
      en: 'Gold Label Global User Agreement | Legal Terms',
    },
    description: {
      hk: '閱讀金利寶環球有限公司用戶使用協議，包括開戶、交易、風險、預付款、出入金及賬戶相關條款。',
      cn: '阅读金利宝环球有限公司用户使用协议，包括开户注册、交易、风险、预付款、出入金及账户相关条款。',
      en: 'Read the Gold Label Global Limited User Agreement covering account opening, trading, risk, margin, deposits, withdrawals and account terms.',
    },
    keywords: {
      hk: '金利寶用戶協議, 金利寶服務條款, 金利寶法律文件',
      cn: '金利宝用户协议, 金利宝服务条款, 金利宝法律文件',
      en: 'Gold Label Global User Agreement, JLBHK terms, legal terms',
    },
  },
  {
    path: '/privacy',
    changefreq: 'yearly',
    priority: '0.4',
    title: {
      hk: '金利寶環球隱私政策｜個人資料私隱',
      cn: '金利宝环球隐私政策｜个人信息保护',
      en: 'Gold Label Global Privacy Policy | Personal Data',
    },
    description: {
      hk: '閱讀金利寶環球有限公司隱私政策，了解個人資料收集、使用、保存、共享、Cookie 及客戶權利。',
      cn: '阅读金利宝环球有限公司隐私政策，了解个人信息收集、使用、保存、共享、Cookie 及客户权利。',
      en: 'Read the Gold Label Global Limited Privacy Policy covering collection, use, retention and protection of personal data, cookies and client rights.',
    },
    keywords: {
      hk: '金利寶隱私政策, 金利寶私隱政策, 個人資料私隱',
      cn: '金利宝隐私政策, 金利宝个人信息保护',
      en: 'Gold Label Global Privacy Policy, JLBHK privacy, personal data',
    },
  },
  {
    path: '/risk-disclosure',
    changefreq: 'yearly',
    priority: '0.4',
    title: {
      hk: '金利寶環球貴金屬風險披露及警示通知',
      cn: '金利宝环球贵金属风险披露及警示通知',
      en: 'Gold Label Global Precious Metals Risk Disclosure',
    },
    description: {
      hk: '閱讀金利寶環球貴金屬風險披露及警示通知，了解槓桿、強制平倉、負餘額、滑點及交易執行等主要風險。',
      cn: '阅读金利宝环球贵金属风险披露及警示通知，了解杠杆、强制平仓、负余额、滑点及交易执行等主要风险。',
      en: 'Read the Gold Label Global Precious Metals Risk Disclosure covering leverage, forced liquidation, negative balances, slippage and execution risks.',
    },
    keywords: {
      hk: '金利寶風險披露, 貴金屬風險, 黃金交易風險',
      cn: '金利宝风险披露, 贵金属风险, 黄金交易风险',
      en: 'Gold Label Global risk disclosure, precious metals risk, leveraged trading risk',
    },
  },
  {
    path: '/grand-opening',
    changefreq: 'weekly',
    priority: '0.6',
    title: {
      hk: '金利寶市場新聞｜黃金白銀分析及貴金屬資訊',
      cn: '金利宝市场新闻｜黄金白银分析及贵金属资讯',
      en: 'Market News and Analysis | Gold Label Global Limited',
    },
    description: {
      hk: '閱讀金利寶環球市場新聞、黃金白銀分析、貴金屬市場動態及投資者教育資訊。',
      cn: '阅读金利宝环球市场新闻、黄金白银分析、贵金属市场动态及投资者教育资讯。',
      en: 'Read Gold Label Global market news, gold and silver analysis, precious metals market updates and investor education content.',
    },
    keywords: {
      hk: '金利寶新聞, 金利寶黃金分析, 黃金新聞, 白銀分析, 貴金屬新聞',
      cn: '金利宝新闻, 金利宝黄金分析, 黄金新闻, 白银分析, 贵金属新闻',
      en: 'JLBHK news, gold analysis, silver analysis, precious metals news, market updates',
    },
  },
];

export const SEO_PAGES: SeoPage[] = BASE_SEO_PAGES.map((page) => {
  const copy = vietnameseSeo[page.path as keyof typeof vietnameseSeo];
  return {
    ...page,
    title: { ...page.title, vn: copy.title },
    description: { ...page.description, vn: copy.description },
    keywords: { ...page.keywords, vn: copy.keywords },
  };
});

export function normalizeSeoPath(pathname: string) {
  if (!pathname) return '/';

  const withoutQuery = pathname.split('?')[0].split('#')[0] || '/';
  const withoutTrailingSlash = withoutQuery.length > 1 ? withoutQuery.replace(/\/+$/, '') : withoutQuery;

  return withoutTrailingSlash || '/';
}

export function getSeoPage(pathname: string) {
  const normalizedPath = normalizeSeoPath(pathname);
  return SEO_PAGES.find((page) => page.path === normalizedPath) || SEO_PAGES[0];
}

export function isSeoLanguageCode(value: string | null | undefined): value is SeoLanguageCode {
  return SEO_LANGUAGES.some((language) => language.code === value);
}

export function getSeoLanguage(languageCode: string | null | undefined) {
  return SEO_LANGUAGES.find((language) => language.code === languageCode) || SEO_LANGUAGES[0];
}

function joinUrlPath(...parts: string[]) {
  const clean = parts
    .filter(Boolean)
    .map((part) => part.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');

  return clean ? `/${clean}` : '/';
}

export function buildLanguagePath(languageCode: SeoLanguageCode, pagePath: string) {
  const language = getSeoLanguage(languageCode);
  return joinUrlPath(language.pathPrefix, pagePath);
}

export function buildAbsoluteUrl(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalizedPath === '/' ? '/' : normalizedPath}`;
}

export function getCanonicalUrl(languageCode: SeoLanguageCode, pagePath: string) {
  return buildAbsoluteUrl(buildLanguagePath(languageCode, pagePath));
}

export function getAlternateUrls(pagePath: string) {
  return SEO_LANGUAGES.map((language) => ({
    ...language,
    href: buildAbsoluteUrl(buildLanguagePath(language.code, pagePath)),
  }));
}
